import * as tslib_1 from "tslib";
import { AsyncSubject } from 'rxjs';
import { EntityType, NavigationProperty, DataProperty, Entity, EntityState, SaveResult, QueryResult, Validator } from 'breeze-client';
import * as moment from 'moment';
import { BreezeService } from './core/breeze.service';
import { DataSet } from './core/helpers/data-set';
import { User } from './domain/helpers/user';
import { AlertsService } from './core/alerts.service';
import { Repeat } from './domain/helpers/repeat';
import { SpendTime } from './domain/helpers/spend-time';
import { Assignment } from './domain/helpers/assignment';
import { Appointment } from './domain/helpers/appointment';
import { Attendant } from './domain/helpers/attendant';
import { RepeatValidation } from './domain/helpers/repeat-validation';
import { Deployment } from './domain/helpers/deployment';
import { Demand } from './domain/helpers/demand';
import { Task } from './domain/helpers/task';
import { Service } from './domain/helpers/service';
import { Shift } from './domain/helpers/shift';
import { Project } from './domain/helpers/project';
import { DeploymentTemplate } from './domain/helpers/deployment-template';
import { DemandTemplate } from './domain/helpers/demand-template';
import { ServiceTemplate } from './domain/helpers/service-template';
import { TaskTemplate } from './domain/helpers/task-template';
import { AttendantTemplate } from './domain/helpers/attendant-template';
import { ShiftTemplate } from './domain/helpers/shift-template';
import { PlanItem } from './domain/helpers/plan-item';
import { Resource } from './domain/helpers/resource';
import { TranslateService } from '@ngx-translate/core';
import { PlanMacro } from './domain/helpers/plan-macro';
import { SettingsService } from './settings.service';
import { BackendSettingsService } from './backend-settings.service';
import { EntityManagerService } from './entity-manager.service';
import { RangeValidator } from './core/validators/range.validator';
import { CurrencyValidator } from './core/validators/currency.validator';
/** Property name to temporary store the original resource oid. */
export var PROP_ORIGINAL_RESOURCE_OID = '$OriginalResourceOid';
/**
 * Encapsulation of breeze.js, this class manage communication with the backend.
 */
var DomainService = /** @class */ (function (_super) {
    tslib_1.__extends(DomainService, _super);
    /**
     * Constructs a DomainService.
     * @param alertsService The Alert service to communicate with user.
     * @memberOf DomainService
     */
    function DomainService(settingsService, backendSettingsService, alertsService, translateService, entityManagerService) {
        var _this = _super.call(this, alertsService, translateService, entityManagerService) || this;
        _this.settingsService = settingsService;
        _this.backendSettingsService = backendSettingsService;
        /**
         * Supported notifications entity types.
         * @private
         * @type {{ [key: string]: Function }}
         * @memberOf DomainService
         */
        _this.registerEntityTypes = {
            // Note that "Foo: Foo" can be shortened to just "Foo" if the key and the value are the same.
            // Register extended entities first to avoid the warning:
            // Registering a constructor that is already used.
            AttendantTemplate: AttendantTemplate,
            DeploymentTemplate: DeploymentTemplate,
            DemandTemplate: DemandTemplate,
            ServiceTemplate: ServiceTemplate,
            ShiftTemplate: ShiftTemplate,
            TaskTemplate: TaskTemplate,
            // The non-extended entities here:
            Repeat: Repeat,
            SpendTime: SpendTime,
            Assignment: Assignment,
            Appointment: Appointment,
            Attendant: Attendant,
            Deployment: Deployment,
            Demand: Demand,
            Service: Service,
            Shift: Shift,
            Task: Task,
            PlanItem: PlanItem,
            Resource: Resource,
            User: User,
            PlanMacro: PlanMacro,
            Project: Project
        };
        return _this;
    }
    DomainService.subtypeOf = function (baseType, subType) {
        if (!baseType) {
            return false;
        }
        if (!subType) {
            return false;
        }
        if (!subType.custom || !subType.custom.baseTypeName) {
            return false;
        }
        if (baseType.name === subType.custom.baseTypeName) {
            return true;
        }
        return DomainService.subtypeOf(baseType, baseType.metadataStore.getEntityTypes().map(function (it) { return it; }).find(function (it) { return it.name === subType.custom.baseTypeName; }));
    };
    DomainService.getNavigationPropertyFor = function (baseType, propertyName) {
        var property = baseType.getNavigationProperty(propertyName);
        if (property) {
            return property;
        }
        var found = baseType.metadataStore.getEntityTypes().map(function (it) { return it; }).find(function (it) { return DomainService.subtypeOf(baseType, it) && it.getNavigationProperty(propertyName) != null; });
        if (found) {
            return found.getNavigationProperty(propertyName);
        }
        return null;
    };
    DomainService.getDataPropertyFor = function (baseType, propertyName) {
        return this.getProperty(baseType, baseType.getDataProperty(propertyName));
    };
    DomainService.getProperty = function (baseType, property) {
        var result = property;
        if (result) {
            return result;
        }
        var shortest_path_to_base = 100000; // Very bad match.
        this.entityTypes.find(function (entityType) {
            if (entityType.isSubtypeOf(baseType)) {
                var test = property;
                if (test) {
                    var step_count = 0;
                    var base = entityType.baseEntityType;
                    while (base !== baseType) {
                        step_count += 1;
                        base = base.baseEntityType;
                    }
                    if (step_count === 0) {
                        result = test;
                        return true;
                    }
                    if (step_count < shortest_path_to_base) {
                        shortest_path_to_base = step_count;
                        result = test;
                    }
                }
            }
            return false;
        });
        return result;
    };
    /**
     * Returns the key of the entity derived from its OID.
     * @static
     * @param {string} oid The OID of the entity.
     * @returns {number} The key of the entity.
     * @memberOf BreezeService
     */
    DomainService.oidToKey = function (oid) {
        if (!oid) {
            return -1;
        }
        var i = oid.trimRight().length - 3;
        if (i < 0) {
            return -1;
        }
        var value = 0;
        if (i >= 0) {
            if (i > 6) {
                i = 6;
            }
            while (i >= 0) {
                var code = DomainService.charToCode(oid, i + 2);
                value += code * Math.pow(36, i);
                i--;
            }
        }
        return value;
    };
    /**
     * Returns the name of the entity type based on OID.
     * @static
     * @param {string} oid The OID of an entity.
     * @returns {string} The class name of the entity type.
     * @memberOf OidTools
     */
    DomainService.oidToClassName = function (oid) {
        var id = DomainService.oidToClassId(oid);
        if (id === -1) {
            return null;
        }
        var value = DomainService.classNames[id];
        return value === undefined ? null : value;
    };
    /**
     * Returns the class ID based on the OID.
     * @static
     * @param {string} oid The OID of an entity.
     * @returns {number} The ID of the entity class.
     * @memberOf OidTools
     */
    DomainService.oidToClassId = function (oid) {
        if (!oid || oid.slice(0, 2) === 'K_') {
            return -1;
        }
        if (oid.length < 2) {
            return -1;
        }
        var value = 0;
        var i = 1;
        while (i >= 0) {
            var code = DomainService.charToCode(oid, i);
            value += code * Math.pow(36, i);
            i--;
        }
        return value;
    };
    /**
     * Creates Oid with class id en key of entity.
     * @static
     * @param {number} classId The class id of the entity
     * @param {number} id The key of the entity.
     * @returns {string} The oid of the entity.
     * @memberOf DomainService
     */
    DomainService.oidWith = function (classId, id) {
        var classOid = DomainService.makeOIDString(classId);
        if (classOid.length === 1) {
            classOid += '0';
        }
        classOid += DomainService.makeOIDString(id);
        return classOid;
    };
    /**
     * Converts a character to a byte.
     * @private
     * @static
     * @param {string} char The character is converted.
     * @returns {number} The byte value of the character.
     * @memberOf OidTools
     */
    DomainService.charToCode = function (char, index) {
        var byte = char.charCodeAt(index);
        var code = byte >= 48 && byte <= 90 ? DomainService.charToCodeList[byte] : -1;
        return code;
    };
    /**
     * Converts number to Oid string
     * @private
     * @static
     * @param {number} value The number
     * @returns {string} The Oid.
     * @memberOf DomainService
     */
    DomainService.makeOIDString = function (value) {
        var Oid = '';
        var CODE_FACTORS = [1, 36, 1296, 46656, 1679616, 60466176, 2176782336];
        var Remainder = value;
        var i = 6;
        var Char_Code = Math.trunc(Remainder / CODE_FACTORS[i]);
        while (i > 0 && Char_Code === 0) {
            i--;
            Remainder = Remainder - Char_Code * CODE_FACTORS[i];
            Char_Code = Math.trunc(Remainder / CODE_FACTORS[i]);
        }
        Oid = DomainService.codeToChar(Char_Code) + '';
        Remainder = Remainder - Char_Code * CODE_FACTORS[i];
        while (i > 0) {
            i--;
            Char_Code = Math.trunc(Remainder / CODE_FACTORS[i]);
            Remainder = Remainder - Char_Code * CODE_FACTORS[i];
            Oid = DomainService.codeToChar(Char_Code) + Oid;
        }
        return Oid;
    };
    /**
     * Converts a code to Oid character
     * @private
     * @static
     * @param {number} code The code
     * @returns {string} The Oid character
     * @memberOf DomainService
     */
    DomainService.codeToChar = function (code) {
        var CODE_TO_CHAR_LIST = [
            '0', '1', '2', '3', '4', '5', '6', '7', '8', '9', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L',
            'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'
        ];
        if (code <= 35) {
            return CODE_TO_CHAR_LIST[code];
        }
        return CODE_TO_CHAR_LIST[code];
    };
    DomainService.prototype.bootInit = function () {
        var _this = this;
        this.officeStart = this.settingsService.settings.frontend.grid.officeHours.enabled.start;
        this.officeEnd = this.settingsService.settings.frontend.grid.officeHours.enabled.end;
        this.initialize('planning');
        DomainService.entityTypes = this.entityManager.metadataStore.getEntityTypes();
        Object.keys(this.registerEntityTypes).forEach(function (entityType) {
            _this.metadataStore.registerEntityTypeCtor(entityType, _this.registerEntityTypes[entityType]);
        });
        this.registerValidator('Appointment', 'Concerns_Oid', Validator.required());
        this.registerValidator('Function', 'SLA', RangeValidator(0, 99));
        this.registerValidator('MacroStep', 'Line', RangeValidator(0, Infinity));
        this.registerValidator('StateCostLink', 'Rate', CurrencyValidator());
    };
    /**
     * Gets an entity by key.
     * @param entityType The entity type to look for.
     * @param key The entity key to match.
     * @param checkLocalCacheFirst Whether to look in the local cache first. Default value is true.
     * @returns An AsyncSubject object to retrieve the requested entity.
     * @memberOf DomainService
     */
    DomainService.prototype.getEntityByKey = function (entityType, key, checkLocalCacheFirst, expand) {
        if (checkLocalCacheFirst === void 0) { checkLocalCacheFirst = true; }
        if (key && key instanceof Array && key.length > 1) {
            switch (entityType.shortName) {
                // TODO: Add more names of virtual classes.
                case 'PlanItem':
                    return _super.prototype.getEntityByKey.call(this, entityType, key, checkLocalCacheFirst, expand);
                default:
                    // Use first key value when class is not virtual.
                    return _super.prototype.getEntityByKey.call(this, entityType, key[0], checkLocalCacheFirst, expand);
            }
        }
        return _super.prototype.getEntityByKey.call(this, entityType, key, checkLocalCacheFirst, expand);
    };
    /**
     * Looks for a location with a given zipcode and house number.
     * If not found a new location entity is created.
     * @param {any} zipCode The zipcode to look for.
     * @param {string} houseNumber The house number to look for.
     * @returns {Observable<QueryResult>} The result of the query.
     * @memberOf DomainService
     */
    DomainService.prototype.locationByZipCode = function (zipCode, houseNumber) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var dataSet, entityType, data, entity, key;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        dataSet = new DataSet(this);
                        dataSet.queryOptions = { 'parameters': { 'zipCode': zipCode, 'number': houseNumber } };
                        dataSet.sourceName = 'LocationByZipCode';
                        entityType = this.getEntityType('Location');
                        dataSet.entityType = entityType;
                        return [4 /*yield*/, dataSet.refresh()];
                    case 1:
                        data = _a.sent();
                        if (data.results.length > 0) {
                            entity = data.results[0];
                            key = entity['Oid'] + '';
                            if (key.indexOf('K_') === 0) {
                                this.entityManager.detachEntity(entity);
                                this.entityManager.addEntity(entity);
                            }
                        }
                        return [2 /*return*/, data];
                }
            });
        });
    };
    /**
     * Method to setup a new master detail relation.
     * @param masterType The entity type of the master.
     * @param masterOid The object identifier of the master.
     * @param detail The detail instance to setup the relation with.
     * @returns The entity that links the master with the detail.
     * @memberOf DomainService
     */
    DomainService.prototype.setupMasterDetail = function (masterType, masterOid, detail) {
        var specialRelation = this.setupSpecialMasterDetail(masterType, masterOid, detail);
        if (specialRelation) {
            // Special relation exist with special logic.
            return specialRelation;
        }
        // Try to resolve relation with standard logic
        var customProp = 'custom';
        var metaDetail = detail.entityType[customProp];
        if (metaDetail.master) {
            // Simple one to many relation. Set foreign key value on detail.
            detail[metaDetail.master] = masterOid;
            return detail;
        }
        // Must look for many to many relation.
        // Getting the types of the possible details of this master.
        var detailTypes = this.getRelatedTypesOf(masterType);
        if (!detailTypes) {
            throw new Error('Can not find many to many relation for: '
                + masterType.shortName + ' <--> '
                + detail.entityType.shortName + '.');
        }
        // This enables the detail.isSubtypeOf method.
        this.getBaseTypeOf(detail.entityType);
        // Get possible many to many link objects for relation.
        var many2many = detailTypes.filter(function (item) {
            // Must has a NavigationProperty of the detail type.
            return (item.navigationProperties.find(function (nav) {
                return (detail.entityType === nav.entityType) || detail.entityType.isSubtypeOf(nav.entityType);
            }) !== undefined);
        });
        if (many2many.length === 0) {
            throw new Error('Can not find many to many relation for: '
                + masterType.shortName + ' <--> '
                + detail.entityType.shortName + '.');
        }
        if (many2many.length > 1) {
            throw new Error('Can not find one specific many to many relation for: '
                + masterType.shortName + ' <--> '
                + detail.entityType.shortName + '.');
        }
        var nav2Detail = this.getRelationProperty(many2many[0], detail.entityType, 'Child');
        var nav2Master = this.getRelationProperty(many2many[0], masterType, 'Parent');
        var values = {};
        values[nav2Master.foreignKeyNames[0]] = masterOid;
        values[nav2Detail.name] = detail;
        return this.addEntity(many2many[0].createEntity(values));
    };
    /**
     * Gets the base type of a type.
     * The current backend cannot return the base type directly in the metadata.
     * @param entityType The entity type.
     * @returns The base type or null when the base type is not a entity type.
     * @memberOf DomainService
     */
    DomainService.prototype.getBaseTypeOf = function (entityType) {
        var customProp = 'custom';
        if (entityType.baseEntityType == null) {
            var customData_1 = entityType[customProp];
            if (customData_1.baseTypeName) {
                entityType.baseEntityType = DomainService.entityTypes.find(function (item) { return item.name === customData_1.baseTypeName; });
                if (entityType.baseEntityType == null) {
                    return null;
                }
            }
            else {
                return null;
            }
            this.getBaseTypeOf(entityType.baseEntityType);
            return entityType.baseEntityType;
        }
        return entityType.baseEntityType;
    };
    /**
     * Gets the navigation property with a certain result type from a link type in a many to many relation.
     * @param linkType The link type of the many to many relation.
     * @param relationType The result type of the navigation property.
     * @param defaultName The default name of the navigation property.
     * @returns The specific navigation property.
     * @memberOf DomainService
     */
    DomainService.prototype.getRelationProperty = function (linkType, relationType, defaultName) {
        var candidates = linkType.navigationProperties.filter(function (nav) {
            return (relationType === nav.entityType) || relationType.isSubtypeOf(nav.entityType);
        });
        if (candidates.length === 1) {
            return candidates[0];
        }
        else {
            var result = candidates.find(function (item) { return item.name === defaultName; });
            if (!result) {
                throw new Error('Can not find specific navigation property on '
                    + linkType.shortName + ' for '
                    + relationType.shortName);
            }
            return result;
        }
    };
    /**
     * Gets the related types of an entity type.
     * @param entityType The entity type.
     * @returns An array of related entity types.
     * @memberOf DomainService
     */
    DomainService.prototype.getRelatedTypesOf = function (entityType) {
        var _this = this;
        // This enables the entityType.isSubtypeOf method.
        this.getBaseTypeOf(entityType);
        var customProp = 'custom';
        var metaMaster = entityType[customProp];
        if (metaMaster.detailTypes) {
            // Already got them.
            return metaMaster.detailTypes;
        }
        if (!metaMaster.detailNames) {
            // This type has no details.
            return null;
        }
        // Filling up detailTypes array.
        metaMaster.detailTypes = [];
        metaMaster.detailNames.forEach(function (name) {
            var detailType = _this.metadataStore.getEntityType(name);
            if (!detailType) {
                throw new Error('Unknown detail type ' + name + '.');
            }
            // This enables the detailType.isSubtypeOf method.
            _this.getBaseTypeOf(detailType);
            metaMaster.detailTypes.push(detailType);
        });
        return metaMaster.detailTypes;
    };
    /**
     * Method to implement special master detail logic when standard logic is not sufficient to setup a relation.
     * @param masterType The entity type of the master.
     * @param masterOid The object identifier of the master.
     * @param detail The detail instance to setup the relation with.
     * @returns The entity that links the master with the detail or null when the relation does not need special logic.
     * @memberOf DomainService
     */
    DomainService.prototype.setupSpecialMasterDetail = function (masterType, masterOid, detail) {
        // TODO: Implement special logic here.
        switch (detail.entityType.shortName) {
            case 'Regulation':
                if (!detail.entityAspect.entityState.isAdded()) {
                    break;
                }
                var regParentOid = '?';
                switch (masterType.shortName) {
                    case 'Contract':
                    case 'ResourceContract':
                        regParentOid = masterOid;
                        break;
                    case 'Resource':
                        if (masterOid.indexOf('K_') === 0) {
                            regParentOid = masterOid;
                        }
                        else {
                            regParentOid = '63' + masterOid.substring(2); // Oid van ResourceContract.
                        }
                        break;
                    default:
                        return null;
                }
                var regLink = this.entityManager.createEntity('ContractLink', {
                    Parent_Oid: regParentOid,
                    Child_Oid: detail['Oid']
                });
                return this.addEntity(regLink);
            case 'StateMembershipLink':
            case 'StateExpertiseLink':
            case 'StateLocalityLink':
            case 'StateCostLink':
            case 'StateResource':
                detail['Parent_Oid'] = masterOid;
                return detail;
            case 'PlanStep':
                detail['Owner_Oid'] = masterOid;
                return detail;
            case 'Taskneed':
                detail['Owner_Oid'] = masterOid;
                return detail;
            case 'TaskTemplate':
                if (masterType.shortName === 'ShiftTemplate') {
                    detail['Shift_Oid'] = masterOid;
                    return detail;
                }
                break;
            case 'Task':
                if (masterType.shortName === 'Project' || masterType.shortName === 'Phase') {
                    detail['Project_Oid'] = masterOid;
                    return detail;
                }
                if (masterType.shortName === 'Shift') {
                    detail['Shift_Oid'] = masterOid;
                    return detail;
                }
                break;
            case 'WorkPattern':
                if (!detail.entityAspect.entityState.isAdded()) {
                    break;
                }
                var parentOid = '?';
                switch (masterType.shortName) {
                    case 'Calendar':
                    case 'ResourceCalendar':
                        parentOid = masterOid;
                        break;
                    case 'Resource':
                        if (masterOid.indexOf('K_') === 0) {
                            parentOid = masterOid;
                        }
                        else {
                            parentOid = '43' + masterOid.substring(2); // Oid van ResourceCalendar.
                        }
                        break;
                    default:
                        return null;
                }
                var link = this.entityManager.createEntity('CalendarLink', {
                    Parent_Oid: parentOid,
                    Child_Oid: detail['Oid']
                });
                return this.addEntity(link);
            case 'MacroStep':
                if (masterType.shortName === 'PlanMacro') {
                    detail['Owner_Oid'] = masterOid;
                    return detail;
                }
        }
        return null;
    };
    /**
     * Sets default values for every newly created entity (just after creation).
     * Specially for non standard (Symagic Server) entity types.
     * @param item The entity to update.
     * @memberOf DomainService
     */
    DomainService.prototype.setDefaultValues = function (user, item) {
        var _this = this;
        _super.prototype.setDefaultValues.call(this, user, item);
        item.entityType.dataProperties.forEach(function (property) {
            if (property.dataType.isDate) {
                switch (property.name) {
                    case 'Start':
                        item[property.name].setHours(_this.officeStart);
                        item[property.name].setMinutes(0);
                        break;
                    case 'Finish':
                        item[property.name].setHours(_this.officeEnd);
                        item[property.name].setMinutes(0);
                        break;
                    case 'Birthday':
                        item[property.name].setFullYear(1980, 0, 1);
                        item[property.name].setHours(0);
                        item[property.name].setMinutes(0);
                        break;
                    case 'Operational':
                        item[property.name].setHours(0);
                        item[property.name].setMinutes(0);
                        break;
                }
            }
            switch (property.name) {
                case 'Color':
                    if (!item['Color']) {
                        switch (item.entityType.shortName) {
                            case 'Attendant':
                            case 'AttendantTemplate':
                                item['Color'] = '#FFFFFF'; // #2816: Attendant heeft altijd dezelfde kleur als zijn Service.
                                break;
                            default:
                                item['Color'] = '#FAD09F'; // Bijna Symagic oranje
                                break;
                        }
                    }
                    break;
            }
        });
        if (item.entityAspect.entityState === EntityState.Added && item['RepeatPattern'] !== undefined) {
            this.assignVirtualRepeat(item);
        }
        var resource = user.Resource;
        switch (item.entityType.shortName) {
            case 'Availability':
                item['Weight'] = 0;
                item['Resource'] = resource;
                item['Start'] = new Date();
                item['Start'].setHours(this.officeStart);
                item['Start'].setMinutes(0);
                item['Finish'] = new Date();
                item['Finish'].setHours(this.officeEnd);
                item['Finish'].setMinutes(0);
                break;
            case 'Appointment':
                item['HoursOfLeave'] = 'PT8H';
                item['Resource'] = resource;
                if (item['SumFactor'] == null) {
                    item['SumFactor'] = 1;
                }
                item['Draft'] = true;
                break;
            case 'Absence':
                item['SumFactor'] = 1;
                break;
            case 'SpendTime':
                item['$currentUser'] = user; // See also: 'set Account_Oid' in app\domain\helpers\spend-time.ts
                item['Resource'] = resource; // Check op HTML5rol weg; zie #1911
                item['Effort'] = 'PT8H';
                item['WorkStatus'] = 'OPEN';
                item['Unforeseen'] = 'NO';
                break;
            case 'Assignment':
                item['Resource'] = resource;
                item['Unforeseen'] = 'NO';
                item['Effort'] = 'PT8H';
                item['SumFactor'] = 1;
                item['Deployment_Oid'] = '';
                break;
            case 'TaskTemplate':
            case 'Task':
                item['Purpose'] = 'OTHER';
                item['EffortBudget'] = 'PT0S';
                item['Effort'] = 'PT0S';
                item['Planned'] = 'PT0S';
                item['SumFactor'] = 1;
                break;
            case 'Demand':
                item['WorkUnit'] = 'PT8H';
                item['SumFactor'] = 1;
                break;
            case 'DeploymentTemplate':
            case 'Deployment':
            case 'DemandTemplate':
                item['SumFactor'] = 1;
                break;
            case 'Shift':
            case 'Service':
            case 'ShiftTemplate':
            case 'ServiceTemplate':
                item['Purpose'] = 'OTHER';
                item['SumFactor'] = 1;
                break;
            case 'Attendant':
            case 'AttendantTemplate':
                item['SumFactor'] = 1;
                break;
            case 'Resource':
                item['Finish'] = new Date(9999, 11, 31);
                item['Skype'] = '';
                item['LinkedIn'] = '';
                break;
            case 'StateResource':
                item['Active'] = true;
                break;
            case 'User':
                item['HTML5Role'] = 'EMPLOYEE';
                item['Language'] = 'nl';
                break;
            default:
                break;
        }
    };
    /**
     * Assigns a virtual repeat (Kind = ONCE) to the entity.
     * @param {Entity} item The entity that gets the repeat.
     */
    DomainService.prototype.assignVirtualRepeat = function (item) {
        var repeatKey = item.entityType.shortName + '\\' + item['Oid'];
        var repeat = this.entityManager.getEntityByKey('Repeat', repeatKey);
        if (!repeat) {
            var entityType = this.entityManager.metadataStore.getEntityType('Repeat', false);
            repeat = entityType.createEntity();
            repeat['Oid'] = repeatKey;
        }
        repeat.Factor = 1;
        repeat.Cycles = -1;
        repeat.UntilDate = new Date();
        repeat.Owner_Oid = item['Oid'];
        repeat.Kind = 'ONCE';
        item['RepeatPattern'] = repeat;
        repeat.entityAspect.setUnchanged();
    };
    /**
     * Creates a copy of an entity.
     * @param item The entity to copy.
     * @returns The copy.
     * @memberOf DomainService
     */
    DomainService.prototype.copy = function (item, deep) {
        var _this = this;
        if (deep === void 0) { deep = true; }
        if (!item) {
            return null;
        }
        var result = _super.prototype.copy.call(this, item, deep);
        // Ik ga ervan uit dat we specifieke properties meenemen voor de deep copy.
        // Een array, alvast voor later als we meer properties meenemen voor deep copy.
        var deepCopyProperties = ['RepeatPattern'];
        if (deep) {
            deepCopyProperties.forEach(function (propertyName) {
                var copyProperty = item.entityType.navigationProperties.find(function (property) {
                    return property.name === propertyName;
                });
                if (copyProperty && item[propertyName]) {
                    var deepItem = null;
                    switch (propertyName) {
                        case 'RepeatPattern':
                            deepItem = item[propertyName];
                            // Specifieke repeat logica
                            if (deepItem['Kind'] === 'ONCE') {
                                result['RepeatPattern_Oid'] = '';
                                result['RepeatPattern'] = null;
                                return;
                            }
                            deepItem = _this.copy(item[propertyName]);
                            deepItem['Owner_Oid'] = result['Oid'];
                            // Fix issue 1051
                            // Owner moet gezet anders raak je hem kwijt in de FieldsetRepeatComponent.constructor
                            // Wat als daar Owner = null dan wordt uitgevoerd: repeat.entityAspect.setUnchanged();
                            deepItem['Owner'] = result;
                            deepItem['Oid'] = result.entityType.shortName + '\\' + deepItem['Owner_Oid'];
                            break;
                    }
                    result[propertyName] = deepItem;
                }
            });
        }
        else {
            deepCopyProperties.forEach(function (propertyName) {
                var copyProperty = item.entityType.navigationProperties.find(function (property) {
                    return property.name === propertyName;
                });
                if (copyProperty) {
                    switch (propertyName) {
                        case 'RepeatPattern':
                            // Specifieke repeat logica.
                            result['RepeatPattern_Oid'] = '';
                            break;
                    }
                }
            });
        }
        return result;
    };
    /**
     * Sets default values for changed entities (just before saving).
     * Specially for non standard (Symagic Server) entity types.
     * @param saveResult
     * @memberOf DomainService
     */
    DomainService.prototype.setDefaultValuesBeforeSave = function (saveResult) {
        var _this = this;
        _super.prototype.setDefaultValuesBeforeSave.call(this, saveResult);
        var deletedPlanItems = [];
        var modifiedPlanItems = [];
        var changes = this.entityManager.getChanges();
        changes.forEach(function (item) {
            switch (item.entityType.shortName) {
                case 'Appointment':
                    if (item['Concerns'] !== null) {
                        item['Color'] = item['Concerns']['Color'];
                        item['SumFactor'] = item['Concerns']['SumFactor'];
                        // Only replace appointment Name if it is empty.
                        if (item['Name'] === '') {
                            item['Name'] = item['Concerns']['Name'];
                        }
                    }
                    break;
                case 'AttendantTemplate':
                case 'Attendant':
                    var bool = item.entityAspect.entityState.isModified;
                    var color = item.entityAspect.originalValues['Color'];
                    if (bool && color) {
                        item['Color'] = '#FFFFFF';
                    }
                    break;
                case 'SpendTime':
                    // Only replace SpentTime Name if it is empty.
                    if (item['Name'] === '' && item['Account'] !== null) {
                        item['Name'] = item['Account']['Name'];
                    }
                    break;
                case 'PlanItem':
                    _this.savePlanItem(item, deletedPlanItems, modifiedPlanItems);
                    break;
                default:
                    break;
            }
        });
        deletedPlanItems.forEach(function (item) {
            _this.entityManager.detachEntity(item);
        });
        saveResult.subscribe(function (data) {
            // Restoring the Item_Oid of modified PlanItems.
            modifiedPlanItems.forEach(function (item) {
                var entity = item['Item'];
                item['Item_Oid'] = entity['Oid'];
                item['Item'] = entity;
                item.entityAspect.setUnchanged();
            });
            // To notify the deletion of virtual instances.
            deletedPlanItems.forEach(function (item) {
                data.entities.splice(0, 0, item);
            });
        });
    };
    /**
     * Rejects current changes, emptying the changeset and restoring entities to their previous state.
     * @memberOf DomainService
     */
    DomainService.prototype.rejectChanges = function () {
        var locations = this.entityManager
            .getChanges()
            .filter(function (item) { return item.entityType.shortName === 'Location' && item['$CHANGED_BY_ENTITY']; });
        locations.forEach(function (item) {
            item.entityAspect.rejectChanges();
            delete item['$CHANGED_BY_ENTITY'];
            delete item['$CHANGED_BY_PROPERTY'];
        });
        _super.prototype.rejectChanges.call(this);
    };
    /**
     * Validates the entity based on the configuration.
     * @param {Entity} item The entity to be validated.
     * @param {EntityConfiguration} configuration The configuration contains the entity fields.
     * @returns {Observable<Array<string>>} An array of errors, thus an empty array if there are no validation errors.
     * @memberOf DomainService
     */
    DomainService.prototype.validateBeforeSave = function (callback, configuration) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var errors, locations, item, entity, property_1, zipCode, number, originalValues, resultSet, location_1, reason_1, changes;
            var _this = this;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        errors = [];
                        locations = this.entityManager
                            .getChanges()
                            .filter(function (item) { return item.entityType.shortName === 'Location' && item['$CHANGED_BY_ENTITY']; });
                        if (!(locations.length > 0)) return [3 /*break*/, 7];
                        item = locations[0];
                        entity = item['$CHANGED_BY_ENTITY'];
                        property_1 = item['$CHANGED_BY_PROPERTY'];
                        delete item['$CHANGED_BY_ENTITY'];
                        delete item['$CHANGED_BY_PROPERTY'];
                        if (!((item['Oid'] + '').lastIndexOf('_') < 2)) return [3 /*break*/, 5];
                        zipCode = item['ZipCode'];
                        number = item['HouseNumber'];
                        if (zipCode === '' && number === '' && item.entityAspect.entityState.isAdded()) {
                            entity[property_1 + '_Oid'] = null;
                            if (entity.entityAspect.entityState.isModified()) {
                                originalValues = entity.entityAspect.originalValues;
                                if (Object.keys(originalValues).filter(function (key) { return key !== property_1 + '_Oid'; }).length === 0) {
                                    entity.entityAspect.setUnchanged();
                                }
                            }
                            item.entityAspect.setDetached();
                            this.validateBeforeSave(callback, configuration);
                            return [2 /*return*/];
                        }
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, this.locationByZipCode(zipCode, number)];
                    case 2:
                        resultSet = _a.sent();
                        if (resultSet.results.length > 0) {
                            entity[property_1] = resultSet.results[0];
                            item.entityAspect.rejectChanges();
                        }
                        else if (item.entityAspect.entityState.isModified()) {
                            location_1 = this.copy(item);
                            entity[property_1] = location_1;
                            location_1['Longitude'] = '';
                            location_1['Latitude'] = '';
                            item.entityAspect.rejectChanges();
                        }
                        this.validateBeforeSave(callback, configuration);
                        return [3 /*break*/, 4];
                    case 3:
                        reason_1 = _a.sent();
                        if (this.addOnce(reason_1, errors)) {
                            this.alertsService.createAlert('danger', reason_1);
                        }
                        return [3 /*break*/, 4];
                    case 4: return [3 /*break*/, 6];
                    case 5:
                        this.validateBeforeSave(callback, configuration);
                        _a.label = 6;
                    case 6: return [2 /*return*/];
                    case 7:
                        changes = this.entityManager.getChanges();
                        changes.forEach(function (item) {
                            if (item.entityAspect.entityState.isDeleted()) {
                                switch (item.entityType.shortName) {
                                    case 'Availability':
                                        if (!_this.validateAvailability(item, _this.backendSettingsService.availabilityClosedTill, errors)) {
                                            item.entityAspect.rejectChanges();
                                        }
                                        break;
                                }
                                return;
                            }
                            var owner = null; // In order not to have to make up new names...
                            // Validating specific entity types.
                            switch (item.entityType.shortName) {
                                case 'Assignment':
                                    if (item['Resource_Oid'] === '') {
                                        var error = 'APP.DOMAIN.VALIDATORS.NEEDS_RESOURCE';
                                        if (_this.addOnce(error, errors)) {
                                            _this.alertsService.createAlert('danger', error);
                                        }
                                        break;
                                    }
                                    if (item['Deployment'] != null || !item.entityAspect.entityState.isAdded()) {
                                        break;
                                    }
                                    // Check of ik Assignment kan createn.
                                    var demand = item['$Deployment.Demand'];
                                    if (!demand) {
                                        var error = 'APP.DOMAIN.VALIDATORS.NEEDS_DEMAND';
                                        if (_this.addOnce(error, errors)) {
                                            _this.alertsService.createAlert('danger', error);
                                        }
                                    }
                                    break;
                                case 'Attendant':
                                case 'AttendantTemplate':
                                    var service = item['Service'];
                                    if (service == null) {
                                        break;
                                    }
                                    if (_this.getValueOf(item, 'Start') < _this.getValueOf(service, 'Start') ||
                                        _this.getValueOf(item, 'Finish') > _this.getValueOf(service, 'Finish')) {
                                        _this.alertsService.createAlert('info', 'APP.DOMAIN.VALIDATORS.ATTENDANT_OUTSIDE_SERVICE');
                                    }
                                    break;
                                case 'Availability':
                                    _this.validateAvailability(item, _this.backendSettingsService.availabilityClosedTill, errors);
                                    break;
                                case 'Demand':
                                case 'DemandTemplate':
                                    var task = _this.getNavigationPropertyLocally(item, 'Task');
                                    if (task == null) {
                                        break;
                                    }
                                    if (_this.getValueOf(item, 'Start') < _this.getValueOf(task, 'Start') ||
                                        _this.getValueOf(item, 'Finish') > _this.getValueOf(task, 'Finish')) {
                                        var error = 'APP.DOMAIN.VALIDATORS.DEMAND_OUTSIDE_TASK';
                                        if (_this.addOnce(error, errors)) {
                                            _this.alertsService.createAlert('danger', error);
                                        }
                                    }
                                    break;
                                case 'Repeat':
                                    if (item['@ValidatedRepeatSplit']) {
                                        delete item['@ValidatedRepeatSplit'];
                                        break;
                                    }
                                    owner = item['Owner'];
                                    if (owner && owner.entityType.shortName === 'Availability') {
                                        _this.validateAvailability(owner, _this.backendSettingsService.availabilityClosedTill, errors);
                                    }
                                    break;
                                case 'Service':
                                case 'ServiceTemplate':
                                    // Fix: Waarden Start en Eind voor een service(template) niet goed bij voor het eerst opslaan #2787
                                    // Validating on finish before start
                                    if (_this.getValueOf(item, 'StartBase') && _this.getValueOf(item, 'FinishBase')) {
                                        if (_this.getValueOf(item, 'StartBase') > _this.getValueOf(item, 'FinishBase')) {
                                            var error = 'APP.DOMAIN.VALIDATORS.FINISH_BEFORE_START';
                                            if (_this.addOnce(error, errors)) {
                                                _this.alertsService.createAlert('danger', error);
                                            }
                                        }
                                    }
                                    var shift = _this.getNavigationPropertyLocally(item, 'Shift');
                                    if (shift == null) {
                                        break;
                                    }
                                    if (_this.getValueOf(item, 'Start') < _this.getValueOf(shift, 'Start') ||
                                        _this.getValueOf(item, 'Finish') > _this.getValueOf(shift, 'Finish')) {
                                        var error = 'APP.DOMAIN.VALIDATORS.SERVICE_OUTSIDE_SHIFT';
                                        if (_this.addOnce(error, errors)) {
                                            _this.alertsService.createAlert('danger', error);
                                        }
                                        break;
                                    }
                                    if (item.entityAspect.originalValues &&
                                        shift.entityAspect.originalValues &&
                                        (item.entityAspect.originalValues['Start'] || item.entityAspect.originalValues['Finish']) &&
                                        (shift.entityAspect.originalValues['Start'] || shift.entityAspect.originalValues['Finish'])) {
                                        var error = 'APP.DOMAIN.VALIDATORS.SHIFT_SERVICE_PERIOD_CHANGE';
                                        if (_this.addOnce(error, errors)) {
                                            _this.alertsService.createAlert('danger', error);
                                        }
                                        break;
                                    }
                                    break;
                                case 'Task':
                                case 'TaskTemplate':
                                    var project = _this.getNavigationPropertyLocally(item, 'Project', item.entityType.shortName === 'Task' ? 'Project' : 'ProjectTemplate');
                                    if (project == null) {
                                        break;
                                    }
                                    if (_this.getValueOf(item, 'Start') < _this.getValueOf(project, 'Start') ||
                                        _this.getValueOf(item, 'Finish') > _this.getValueOf(project, 'Finish')) {
                                        var error = 'APP.DOMAIN.VALIDATORS.TASK_OUTSIDE_PROJECT';
                                        if (_this.addOnce(error, errors)) {
                                            _this.alertsService.createAlert('danger', error);
                                        }
                                    }
                                    break;
                                case 'Taskneed':
                                    var categoryName = DomainService.oidToClassName(item['Category_Oid']);
                                    var className = DomainService.oidToClassName(item['Owner_Oid']);
                                    // The only restraint for taskneeds is that shifts and shift templates may not have more than one competence.
                                    if (categoryName !== 'Function' || (className !== 'Shift' && className !== 'ShiftTemplate')) {
                                        break;
                                    }
                                    owner = _this.getNavigationPropertyLocally(item, 'Owner', className);
                                    var taskneeds = _this.getRelationsLocally(owner, 'Taskneed', 'Owner');
                                    var otherFunction = taskneeds.findIndex(function (taskneed) {
                                        return taskneed['Category_Oid'] !== item['Category_Oid'] && DomainService.oidToClassName(taskneed['Category_Oid']) === 'Function';
                                    });
                                    if (otherFunction !== -1) {
                                        var error = 'APP.DOMAIN.VALIDATORS.ONLY_ONE_FUNCTION';
                                        if (_this.addOnce(error, errors)) {
                                            _this.alertsService.createAlert('danger', error);
                                        }
                                    }
                                    break;
                            }
                            // Validating on finish before start
                            if (_this.getValueOf(item, 'Start') && _this.getValueOf(item, 'Finish')) {
                                if (_this.getValueOf(item, 'Start') > _this.getValueOf(item, 'Finish')) {
                                    var error = 'APP.DOMAIN.VALIDATORS.FINISH_BEFORE_START';
                                    if (_this.addOnce(error, errors)) {
                                        _this.alertsService.createAlert('danger', error);
                                    }
                                }
                            }
                            _this.validateFieldsBeforeSave(item, configuration, errors);
                        });
                        if (errors.length > 0) {
                            callback(errors);
                        }
                        else {
                            callback();
                        }
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Validation of a changed availability entity.
     * Is used in the method validateBeforeSave.
     * @private
     * @param {Entity} item The availability entity
     * @param {Date} availabilityClosedTill Date to check if entity is allowed change.
     * @param {string[]} errors Array that collects the errors.
     * @returns {boolean} True when evering is OK.
     * @memberOf DomainService
     */
    DomainService.prototype.validateAvailability = function (item, availabilityClosedTill, errors) {
        var start = moment(item['Start']);
        var test = item.entityAspect.originalValues['Start'];
        if (test) {
            var original = moment(test);
            if (item.entityAspect.entityState.isDeleted() || !original.isAfter(start)) {
                start = original;
            }
        }
        start = start.startOf('day');
        if (start.isBefore(availabilityClosedTill)) {
            var error = 'APP.DOMAIN.AVAILABILITY_FROZEN';
            if (this.addOnce(error, errors)) {
                this.alertsService.createAlert('danger', error);
            }
            return false;
        }
        return true;
    };
    /**
     * Gets the value of the entity according the given property path.
     * @private
     * @param {Entity} entity The entity with the value
     * @param {string} propertyPath The property path to the value.
     * @returns {*} The value when it exists otherwise null.
     * @memberOf DomainService
     */
    DomainService.prototype.getValueOf = function (entity, propertyPath) {
        if (!entity || !propertyPath) {
            return null;
        }
        var result = entity;
        var segments = propertyPath.split('.');
        for (var i = 0; i < segments.length; i++) {
            result = result[segments[i]];
            if (result == null) {
                // In this way all is alright when result === false.
                return null;
            }
        }
        return result;
    };
    /**
     * PlanItems are virtual/calculated entities, changes cannot be saved.
     * This method implements logic to translate the intentions of the user
     * when changing a PlanItem.
     * @private
     * @param {Entity} planItem The PlanItem with a new state.
     * @param {Entity[]} deletedItems Array to collect the deleted PlanItems.
     * @param {Entity[]} modifiedItems Array to collect the modified PlanItems.
     * @returns void;
     * @memberOf DomainService
     */
    DomainService.prototype.savePlanItem = function (planItem, deletedItems, modifiedItems) {
        // The entity presented by the PlanItem.
        var presentedEnity = this.entityManager.getEntityByKey(planItem['ItemClass'], planItem['Item_Oid']);
        if (!presentedEnity) {
            return;
        }
        var repeatKey = presentedEnity['RepeatPattern_Oid'];
        if (!repeatKey) {
            // Presented entity has no RepeatPattern_Oid property treat it as ONCE.
            repeatKey = '\\';
        }
        switch (planItem.entityAspect.entityState) {
            case EntityState.Deleted:
                deletedItems.push(planItem);
                if (repeatKey.indexOf('\\') === -1) {
                    // The PlanItem is an instance of a repeat pattern of the presented entity.
                    // Get the start that marks the instance in the repeat pattern.
                    var start = planItem['Start'];
                    if (planItem.entityAspect.originalValues && planItem.entityAspect.originalValues['Start']) {
                        start = planItem.entityAspect.originalValues['Start'];
                    }
                    // We interrupt the repeat pattern at the found start.
                    var interrupt = this.entityManager.createEntity('Interrupt', { Owner_Oid: repeatKey, Date: start });
                    this.addEntity(interrupt);
                }
                else {
                    // The PlanItem is the instance it presents.
                    // We delete the presented entity.
                    this.setDeleted(presentedEnity);
                }
                break;
            case EntityState.Modified:
                if (repeatKey.indexOf('\\') === -1) {
                    // The PlanItem is an instance of a repeat pattern of the presented entity.
                    // Get the start that marks the instance in the repeat pattern.
                    var start = planItem['Start'];
                    if (planItem.entityAspect.originalValues['Start']) {
                        start = planItem.entityAspect.originalValues['Start'];
                    }
                    // We interrupt the repeat pattern at the found start.
                    var interrupt = this.entityManager.createEntity('Interrupt', { Owner_Oid: repeatKey, Date: start });
                    this.addEntity(interrupt);
                    // We create a copy the presented entity
                    var copyEntity = this.copy(presentedEnity, false);
                    modifiedItems.push(planItem);
                    // We change its Start and Finish according the PlanItem.
                    copyEntity['Start'] = planItem['Start'];
                    copyEntity['Finish'] = planItem['Finish'];
                    if (planItem.entityAspect.originalValues['Effort']) {
                        copyEntity['Effort'] = planItem['Effort']; // Gaat dit goed? Niet getest hier onder wel.
                    }
                    this.addEntity(copyEntity);
                    // We keep a reference of the copy on the PlanItem.
                    planItem['Item'] = copyEntity;
                    planItem.entityAspect.setUnchanged();
                }
                else {
                    // The PlanItem is the instance it presents.
                    // We change its Start and Finish according the PlanItem.
                    // Only when user has changed property, see issue:
                    // Taak: Werkdruk wordt niet aangepast na aanpassen #2541
                    if (planItem.entityAspect.originalValues['Start']) {
                        presentedEnity['Start'] = planItem['Start'];
                    }
                    if (planItem.entityAspect.originalValues['Finish']) {
                        presentedEnity['Finish'] = planItem['Finish'];
                    }
                    if (planItem.entityAspect.originalValues['Effort']) {
                        presentedEnity['Effort'] = planItem['Effort']; // Getest: Effort gaat mee in edit agenda.
                    }
                    planItem['Item'] = presentedEnity;
                    planItem.entityAspect.setUnchanged();
                }
                break;
        }
    };
    /**
     * Validate fields defined in the configuration and puts the found errors in the given errors array.
     * The method is used in validateBeforeSave to support form validation.
     * @private
     * @param {Entity} item The entity that is validated
     * @param {EntityConfiguration} configuration The configuration used by the form knowing which fields are depicted
     * @param {string[]} errors The array collecting the found errors.
     * @memberOf DomainService
     */
    DomainService.prototype.validateFieldsBeforeSave = function (item, configuration, errors) {
        var _this = this;
        if (!configuration || !item) {
            return;
        }
        if (item.entityType.shortName === 'Repeat' && item['Owner']) {
            var entity = item['Owner'];
            if (entity.entityType.shortName === configuration.entity) {
                var field = configuration.fields.find(function (fld) { return fld.editor === 'repeat'; });
                if (field) {
                    if (this.validateRepeatPattern(entity, field.name, errors)) {
                        this.alertsService.createAlert('danger', errors[errors.length - 1], field.name);
                    }
                    return;
                }
            }
        }
        if (configuration.entity !== item.entityType.shortName) {
            return;
        }
        var missing = [];
        // Validating entity fields.
        configuration.fields.forEach(function (field) {
            switch (field.editor) {
                case 'repeat':
                    if (_this.validateRepeatPattern(item, field.name, errors)) {
                        _this.alertsService.createAlert('danger', errors[errors.length - 1], field.name);
                    }
                    break;
                default:
                    if (field.required &&
                        field.name !== '@this') {
                        var value = _this.getValueOf(item, field.name);
                        if (value == null || value === '') {
                            // Preventing redundant missing argument
                            if (errors.indexOf('APP.DOMAIN.VALIDATORS.ONLY_ONE_FUNCTION') === -1 || field.labelKey !== 'APP.MISC.TASKNEED.CATEGORY') {
                                missing.push(field.labelKey);
                            }
                        }
                    }
                    break;
            }
        });
        if (missing.length > 0) {
            if (this.addOnce('APP.DOMAIN.VALIDATORS.REQUIRED_FIELD_MISSING', errors)) {
                this.alertsService.createAlert('danger', 'APP.DOMAIN.VALIDATORS.REQUIRED_FIELD_MISSING', missing);
            }
        }
    };
    /**
     * Validates the repeat pattern of an entity.
     * @private
     * @param {Entity} item The entity that owns the repeat
     * @param {string} propertyName The property name that holds the repeat.
     * @param {string[]} errors The array collecting the errors.
     * @returns True when a new error is found.
     * @memberOf DomainService
     */
    DomainService.prototype.validateRepeatPattern = function (item, propertyName, errors) {
        var repeat = this.getValueOf(item, propertyName);
        if (!repeat) {
            return false;
        }
        var start;
        if (item) {
            start = item['Start'];
        }
        if (!start) {
            start = repeat.Start;
        }
        var error = null;
        var validation = repeat.validateWith(start);
        switch (repeat.validateWith(start)) {
            case RepeatValidation.WeekDayMismatch:
                error = 'APP.DOMAIN.VALIDATORS.REPEAT_WEEKDAY_MISMATCH';
                break;
            case RepeatValidation.OccurrenceOrderMismatch:
                error = 'APP.DOMAIN.VALIDATORS.REPEAT_ORDER_MISMATCH';
                break;
            case RepeatValidation.DateMismatch:
                error = 'APP.DOMAIN.VALIDATORS.REPEAT_DATE_MISMATCH';
                break;
            case RepeatValidation.MonthMismatch:
                error = 'APP.DOMAIN.VALIDATORS.REPEAT_MONTH_MISMATCH';
                break;
            case RepeatValidation.UntilDateBeforeStart:
                error = 'APP.DOMAIN.VALIDATORS.REPEAT_UNTIL_BEFORE_START';
                break;
        }
        return this.addOnce(error, errors);
    };
    /**
     * Adds a string value to a string array when value does not exist in array.
     * Otherwise value is not added to array.
     * @private
     * @param {String} value The value to add only once.
     * @param {String[]} array The array that gets the value only once.
     * @returns True when value is added.
     * @memberOf DomainService
     */
    DomainService.prototype.addOnce = function (value, array) {
        if (!value || array.findIndex(function (item) { return item === value; }) !== -1) {
            return false;
        }
        array.push(value);
        return true;
    };
    /**
     * The list is used to convert OID's to keys.
     * @private
     * @memberOf BreezeService
     */
    DomainService.charToCodeList = [
        -1, -1, -1, -1, -1, -1, -1, -1, -1, -1,
        -1, -1, -1, -1, -1, -1, -1, -1, -1, -1,
        -1, -1, -1, -1, -1, -1, -1, -1, -1, -1,
        -1, -1, -1, -1, -1, -1, -1, -1, -1, -1,
        -1, -1, -1, -1, -1, -1, -1, -1, 0, 1,
        2, 3, 4, 5, 6, 7, 8, 9, -1, -1,
        -1, -1, -1, -1, -1, 10, 11, 12, 13, 14,
        15, 16, 17, 18, 19, 20, 21, 22, 23, 24,
        25, 26, 27, 28, 29, 30, 31, 32, 33, 34,
        35
    ];
    /**
     * The names of classes by their ID.
     * @static
     * @memberOf BreezeService
     */
    DomainService.classNames = {
        20: 'Constraint', 21: 'Profile', 22: 'Evaluation', 23: 'Violation', 31: 'Repeat', 40: 'Activity', 41: 'Assignment', 50: 'Task',
        51: 'Demand', 52: 'Shift', 53: 'Taskneed', 60: 'Project', 61: 'Phase', 70: 'Category', 71: 'Absence', 72: 'Process', 73: 'Function',
        74: 'Team', 75: 'Location', 76: 'Regulation', 80: 'Assets', 81: 'Expertise', 82: 'Membership', 83: 'Locality', 84: 'Contract', 85: 'Context',
        90: 'Resource', 91: 'Tool', 92: 'StaffMember', 93: 'ParentChildLink', 94: 'TimeLink', 95: 'ExpertiseLink', 96: 'StateTimeLink', 97: 'StateWeightLink',
        98: 'StateRankLink', 99: 'StateExpertiseLink', 100: 'MembershipLink', 101: 'LocalityLink', 102: 'ContractLink', 103: 'StateContractLink', 104: 'StateMembershipLink',
        105: 'StateLocalityLink', 106: 'Interrupt', 107: 'Deployment', 108: 'StateCategory', 109: 'Calendar', 110: 'WorkPattern', 111: 'CalendarLink', 112: 'ResourceCalendar',
        113: 'Rule', 114: 'ResourceContract', 115: 'Member', 116: 'Sequence', 117: 'Appointment', 118: 'CostLink', 119: 'Tariff', 120: 'TariffGroup', 121: 'StateCostLink',
        122: 'StateResource', 123: 'LogicLink', 124: 'Universe', 125: 'Availability', 126: 'User', 127: 'UserRole', 128: 'Authorization', 129: 'AuthorizationLink',
        130: 'UserRoleLink', 131: 'ProjectTemplate', 132: 'PhaseTemplate', 133: 'TaskTemplate', 134: 'DemandTemplate', 135: 'DeploymentTemplate', 136: 'MemberTemplate',
        137: 'Service', 138: 'Attendant', 139: 'ShiftTemplate', 140: 'ServiceTemplate', 141: 'AttendantTemplate', 142: 'Facility', 143: 'UserData', 144: 'BlobData',
        145: 'ResourceTariffGroup', 146: 'WorkTariffGroup', 147: 'SpendTime', 148: 'ProjectExpense', 149: 'ResourceExpense', 150: 'Customer', 151: 'Code', 152: 'PlanStrategy',
        153: 'PlanStep', 155: 'TaskCalendar', 156: 'PrivateWorkPattern', 157: 'PlanMacro', 158: 'MacroStep', 159: 'Subject', 160: 'Budget', 161: 'BudgetLink',
        162: 'AssignmentHistory', 163: 'WorkBudget', 164: 'Value', 165: 'Stakeholder', 166: 'Contact', 167: 'FacilityCalendar', 168: 'FacilityTariffGroup', 169: 'FacilityContract',
        170: 'FacilityMembership', 171: 'FacilityExpertise', 172: 'FacilityLocality', 173: 'TaskTariffGroup', 174: 'TaskTemplateTariffGroup', 175: 'ProjectTemplateTariffGroup', 176: 'AttendantHistory', 177: 'AssignmentTemplate'
    };
    return DomainService;
}(BreezeService));
export { DomainService };
